import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import Drawer from '../../../components/Drawer'
import Button from '../../../components/Button'
import SkeletonMultipleSectionsForm from './Skeletons/SkeletonMultipleSectionsForm'

import useManageDrawer from '../../../hooks/formHooks/useManageDrawer'
import useComponentWillUnmount from '../../../hooks/useComponentWillUnmount'

import { formSuccessSubmittedSelector } from '../../../modules/selectors/forms'

import useStyles from '../../../styles/common/drawerForms'

function FormDrawerWrapper({
  title,
  description,
  children,
  // button props
  OpenDrawerTrigger,
  showOpenButton = true,
  openButtonText,
  openBtnClassName,
  // form props
  formName,
  closeOnSubmit = true,
  onAfterFormClose,
  // loading props
  SkeletonFormComponent,
  skeletonSectionsNumber,
  isFormLoading
}) {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const formWasSubmitted = useSelector(formSuccessSubmittedSelector(formName))

  // managing FormDrawerWrapper open state is doing internally from children components
  const { isFormOpen, openDrawerForm, handleFormClose } = useManageDrawer({
    formName: formName,
    onAfterFormClose
  })

  // when form is unmounted with open state (e.g. redirection inside drawer happens), explicitly close it
  // we also need to use custom unmount hook to track real unmount action (usual useEffect doesn't work)
  useComponentWillUnmount(() => {
    if (isFormOpen) {
      handleFormClose()
    }
  })

  useEffect(() => {
    if (closeOnSubmit && formWasSubmitted) {
      handleFormClose()
    }
  }, [dispatch, handleFormClose, formWasSubmitted, closeOnSubmit])

  return (
    <>
      {showOpenButton &&
        (OpenDrawerTrigger ? (
          <OpenDrawerTrigger onClick={openDrawerForm} />
        ) : (
          <Button onClick={openDrawerForm} className={openBtnClassName}>
            {t(openButtonText)}
          </Button>
        ))}
      <Drawer isOpen={isFormOpen} onClose={handleFormClose} title={title} description={description}>
        <div className={classes.formContainer}>
          {isFormLoading ? (
            SkeletonFormComponent ? (
              <SkeletonFormComponent />
            ) : (
              <SkeletonMultipleSectionsForm skeletonSectionsNumber={skeletonSectionsNumber} />
            )
          ) : (
            children
          )}
        </div>
      </Drawer>
    </>
  )
}

FormDrawerWrapper.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  children: PropTypes.node.isRequired,
  // button props
  OpenDrawerTrigger: PropTypes.func,
  showOpenButton: PropTypes.bool,
  openButtonText: PropTypes.string,
  openBtnClassName: PropTypes.string,
  // form props
  formName: PropTypes.string.isRequired,
  closeOnSubmit: PropTypes.bool,
  onAfterFormClose: PropTypes.func,
  // loading props
  SkeletonFormComponent: PropTypes.func,
  skeletonSectionsNumber: PropTypes.number,
  isFormLoading: PropTypes.bool
}

export default FormDrawerWrapper
